import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {OfferInfo} from "~/app/core/model/offer-info";
import {momentNow, toDate, toMoment} from "~/app/core/model/date-str";
import {OfferItemView} from "~/app/components/offer-item/offer-item-view";
import {OfferItemAction, OfferItemActionType} from "~/app/components/offer-item/offer-item-actions";
import {Settings} from "~/app/core/model/settings";
import {ApiService} from "~/app/core/api.service";
import {getNextDate, getNextDaySchedule, getSameDaySchedule, isWithinWeeklyHours, toDayScheduleStr, toWeeklyHoursCouponString} from "~/app/core/model/weekly-hours";

// Imported in offer.module.ts
@Component({
    selector: "OfferCoupon",
    templateUrl: "./offer-coupon.component.html",
    styleUrls: ["./offer-coupon.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfferCouponComponent implements OnInit, AfterViewInit, OnDestroy, DoCheck {
    @Output() readonly action = new EventEmitter<OfferItemAction>();

    @Input() mysteryOffer: boolean = false;
    @Input() showRedeemNow: boolean;
    @Input() imageBanner: string;
    @Input() unavailable: boolean;
    @Input() showExpiration: boolean;
    @Input() milesAway: number;
    @Input() cityName: string;
    @Input() displayTime: moment.Moment = momentNow();

    settings: Settings;

    isOnlineOnly: boolean = false;
    hasNotice: boolean = false;
    noticeClassList: string = null;
    noticeText: string = null;

    @Input() set offerInfo(offerInfo: OfferInfo) {
        if (this._offerInfo !== offerInfo) {
            this._offerInfo = offerInfo;
            this._daysRemaining = null;
            this.isOnlineOnly = offerInfo && !!offerInfo.onlineOnly;
            this.cd.detectChanges();
        }
    }
    get offerInfo(): OfferInfo {
        return this._offerInfo;
    }

    @Input() set viewFraction(fraction: number) {
        if (this._fraction !== fraction) {
            this.updateViewExpansion(fraction);
        }
    }

    private _offerInfo: OfferInfo;

    private _currentDate: Date = null;
    private _showRedeemed: boolean = null;
    private _redeemed: boolean = null;
    private _obscured: boolean = null;
    private _daysRemaining: number = null;
    private _daysTillAvailable: number = null;
    private _fraction: number;
    private _closeVisible: boolean;

    get free(): boolean {
        // console.log("REFRESH ---------------------------- OfferCoupon) free:" + (this.offerInfo ? this.offerInfo.id : "NONE"));
        return OfferItemView.isOfferFree(this.offerInfo);
    }

    get shippedFree(): boolean {
        // console.log("REFRESH ---------------------------- OfferCoupon) free:" + (this.offerInfo ? this.offerInfo.id : "NONE"));
        return OfferItemView.isOfferShippedFree(this.offerInfo);
    }

    get shippedAdditional(): boolean {
        // console.log("REFRESH ---------------------------- OfferCoupon) free:" + (this.offerInfo ? this.offerInfo.id : "NONE"));
        return OfferItemView.isOfferShippedAdditional(this.offerInfo);
    }

    get freeBannerMessage(): string {
        if (!this.free) return null;
        if (this.isOnlineOnly) {
            if (this.shippedFree) {
                return "100% FREE! Shipping included.";
            } else if (this.shippedAdditional) {
                return null;
            }
        }
        return "100% FREE! No purchase necessary.";
    }

    get mysteryMessage(): string {
        if (!this.free) return null;
        if (this.isOnlineOnly) {
            if (this.shippedFree) {
                return "100% FREE!";
            } else if (this.shippedAdditional) {
                return null;
            }
        }
        return "100% FREE!";
    }

    get sample(): boolean {
        return !this.offerInfo ? false : !!this.offerInfo.sample;
    }

    get newOffer(): boolean {
        return OfferItemView.isOfferNew(this.settings.calculateCurrentTime(), this.offerInfo);
    }

    @Input()
    set redeemed(redeemed: boolean) {
        this._redeemed = redeemed;
        this.cd.detectChanges();
    }

    get redeemed(): boolean {
        return this._redeemed != null ? this._redeemed : !this.obscured && OfferItemView.isOfferRedeemed(this.offerInfo, this.settings.calculateCurrentTime());
    }

    get repeatUnlockable(): boolean {
        return this.settings.gamificationEnabled && OfferItemView.isOfferRepeatUnlockable(this.offerInfo, this.settings.calculateCurrentTime());
    }

    get redeemedInPast(): boolean {
        return this._redeemed != null ? this._redeemed : this.offerInfo && !!this.offerInfo.redeemedDate;
    }

    get active(): boolean {
        return !this.repeatUnlockable && !this.redeemed;
    }

    @Input()
    set obscured(obscured: boolean) {
        this._obscured = obscured;
        this.cd.detectChanges();
    }

    get obscured(): boolean {
        return this._obscured != null && this._obscured;
    }

    @Input()
    set showRedeemed(showRedeemed: boolean) {
        this._showRedeemed = showRedeemed;
        this.cd.detectChanges();
    }

    get showRedeemed(): boolean {
        return !!this._showRedeemed;
    }

    get favorited(): boolean {
        return this._offerInfo && this._offerInfo.id > 0 && !!this._offerInfo.favorite && !!this._offerInfo.revealed && this.settings.gamificationFavoriteMode;
    }

    get favoritable(): boolean {
        return this._offerInfo && this._offerInfo.id > 0 && !this._offerInfo.favorite && !!this._offerInfo.revealed && this.settings.gamificationFavoriteMode;
    }

    get canFavorite(): boolean {
        return this._offerInfo && (this._offerInfo.favorite || (this._offerInfo.favoriteCost <= this.api.tickets_getTicketCount()));
    }

    get expired(): boolean {
        return this.daysRemaining === 0;
    }

    get daysRemaining(): number {
        if (this.showExpiration) {
            if (this._daysRemaining == null) {
                this._daysRemaining = OfferItemView.calculateDaysRemaining(this._currentDate, this.offerInfo);
            }
            return this._daysRemaining;
        } else {
            return null;
        }
    }

    get daysTillAvailable(): number {
        if (this.redeemed) {
            if (this._daysTillAvailable == null) {
                this._daysTillAvailable = OfferItemView.calculateDaysTillAvailable(this._currentDate, this.offerInfo);
            }
            return this._daysTillAvailable;
        } else {
            return null;
        }
    }

    get canRedeemNow(): boolean {
        return this.showRedeemNow && !this.outsideOfHours;
    }

    get outsideOfHours(): boolean {
        return this.offerInfo && this.offerInfo.weeklyHours && !isWithinWeeklyHours(this.offerInfo.weeklyHours, this.displayTime);
    }

    get insideOfHours(): boolean {
        return this.offerInfo && this.offerInfo.weeklyHours && !this.outsideOfHours;
    }

    get viewed(): boolean {
        if (this.offerInfo && this.offerInfo.watchedVideo) {
            return true;
        }
        return false;
    }

    get solicitBounceback(): boolean {
        return this.redeemedInPast && OfferItemView.isOfferReadyForBounceback(this.settings.calculateCurrentTime(), this.offerInfo);
    }

    get offerName(): string {
        return !this.offerInfo ? "" : this.offerInfo.name;
    }

    get offerTerms(): string {
        if (!this.offerInfo || !this.offerInfo.terms) {
            return "";
        } else {
            const description = this.offerInfo.terms.trim();
            return description;
        }
    }

    get offerAdditionalTerms(): string {
        if (!this.offerInfo || !this.offerInfo.additionalTerms || this.offerInfo.additionalTerms.length === 0) {
            return null;
        } else {
            const description = this.offerInfo.additionalTerms;
            return description;
        }
    }

    get offerWeeklyHours(): string {
        return !this.offerInfo || !this.offerInfo.weeklyHours ? null : toWeeklyHoursCouponString(this.offerInfo.weeklyHours);
    }

    get offerExpirationDate(): Date {
        return !this.offerInfo ? null : toDate(this.offerInfo.expirationDate);
    }

    get offerRedeemedDate(): Date {
        return !this.offerInfo ? null : toDate(this.offerInfo.redeemedDate);
    }

    get offerCompany(): string {
        return !this.offerInfo ? "" : this.offerInfo.companyName;
    }

    get classList(): string {
        const classes = [];
        if (this.free) classes.push("type-free");
        else if (!this.free) classes.push("type-coupon");
        if (this.free && this.isOnlineOnly) classes.push("online-only");
        if (!this.unavailable && !this.outsideOfHours && !this.expired && !this.redeemed) classes.push("status-available");
        if (this.redeemed) classes.push("status-redeemed");
        if ((this.expired || this.unavailable || this.outsideOfHours) && !this.redeemed) {
            if (this.outsideOfHours) {
                classes.push("status-outofhours");
            } else {
                classes.push("status-expired");
            }
        }
        return classes.join(" ");
    }

    constructor(
        private readonly api: ApiService,
        private readonly cd: ChangeDetectorRef
    ) {
        // Use the component constructor to inject providers.

        this.settings = api.device_loadSettings();
        this._currentDate = this.settings.calculateCurrentTime();
    }

    ngOnInit(): void {
        // empty
        this.updateNotice();
    }

    ngOnDestroy(): void {
    }

    ngAfterViewInit(): void {
    }

    ngDoCheck(): void {
        this.updateNotice();
    }

    onAction(action: OfferItemAction): void {
        this.action.emit(action);
    }

    onTapOpenButton(): void {
        this.action.emit({ type: OfferItemActionType.tapOpen });
    }

    onTapFavoriteButton(): void {
        this.action.emit({ type: OfferItemActionType.tapFavorite });
    }

    updateViewExpansion(fraction: number) {
        const lastFraction = this._fraction;
        this._fraction = fraction;
        if (fraction > lastFraction) {
            if (!this._closeVisible) {
                // run in zone because sometimes animation is apparently running outside of angular visibility
                this._closeVisible = true;
                this.cd.detectChanges();
            }
        }
        if (fraction < lastFraction && lastFraction >= 1.0) {
            this.beginMinimizing();
        } else if (fraction > lastFraction && lastFraction <= 0.0) {
            this.beginExpanding();
        }
        if (fraction < lastFraction && fraction <= 0.01) {
            this.fullyMinimized();
        } else if (fraction > lastFraction && fraction >= 0.99) {
            this.fullyExpanded();
        }
    }

    private beginExpanding() {
        this.action.emit({ type: OfferItemActionType.whenExpanding });
    }

    private beginMinimizing() {
        this.cd.detectChanges();
        this.action.emit({ type: OfferItemActionType.whenMinimizing });
    }

    private fullyExpanded() {
        if (!this._closeVisible) {
            // run in zone because sometimes animation is apparently running outside of angular visibility
            this._closeVisible = true;
            this.action.emit({ type: OfferItemActionType.whenExpanded });
            this.cd.detectChanges();
        }
    }

    private fullyMinimized() {
        if (this._closeVisible) {
            // run in zone because sometimes animation is apparently running outside of angular visibility
            this._closeVisible = false;
            this.action.emit({ type: OfferItemActionType.whenMinimized });
            this.cd.detectChanges();
        }
    }

    private setNotice(classList: string, text: string) {
        if (classList !== this.noticeClassList || text !== this.noticeText) {
            this.hasNotice = !!classList && !!text;
            this.noticeClassList = classList;
            this.noticeText = text;
            this.cd.markForCheck();
        }
    }

    private updateNotice() {
        this.setNotice(null, null);
        if (this.active) {
            if (this.expired) this.setNotice("notice noticeExpired", "Expire");
            // not expired
            if (this.unavailable) this.setNotice("notice noticeExpired", "Unavailable");
            // available
            if (!this.showRedeemed) {
                if (this.outsideOfHours) {
                    const nextDate = getNextDate(this.offerInfo.weeklyHours, this.displayTime);
                    if (nextDate) {
                        if (nextDate === this.displayTime) {
                            this.setNotice("notice noticeLaterToday", "Available " + toDayScheduleStr(getNextDaySchedule(this.offerInfo.weeklyHours, this.displayTime)) + " today");
                        } else {
                            const tomorrow = this.displayTime.clone().add(1, "days");
                            if (tomorrow.toDate().getDate() === nextDate.toDate().getDate()) {
                                this.setNotice("notice noticeLaterAnotherDay", "Available " + toDayScheduleStr(getNextDaySchedule(this.offerInfo.weeklyHours, this.displayTime)) + " tomorrow");
                            } else {
                                const dayString = nextDate.format('dddd');
                                this.setNotice("notice noticeLaterAnotherDay", "Available " + toDayScheduleStr(getNextDaySchedule(this.offerInfo.weeklyHours, this.displayTime)) + " " + dayString);
                            }
                        }
                    }
                } else if (this.insideOfHours) this.setNotice("notice noticeEndsSoon", "Available " + toDayScheduleStr(getSameDaySchedule(this.offerInfo.weeklyHours, this.displayTime)) + " today");
                else if (this.daysRemaining === 1) this.setNotice("notice noticeLastDay", "Last day");
                else if (this.daysRemaining === 2) this.setNotice("notice noticeEndsTomorrow", "Ends tomorrow");
                else if (this.daysRemaining === 3) this.setNotice("notice noticeEndsSoon", "Ends in " + (this.daysRemaining - 1) + " days");
            }
        } else {
            if (this.offerInfo.nextAvailableDate && this.daysTillAvailable > 0) {
                const nextAvailableDate = toMoment(this.offerInfo.nextAvailableDate);
                if (this.daysTillAvailable === 1) this.setNotice("notice noticeNextAvailableFuture", "Available again tomorrow");
                else if (this.daysTillAvailable > 2 && this.daysTillAvailable < 7) {
                    const dayString = nextAvailableDate.format('dddd');
                    this.setNotice("notice noticeLaterAnotherDay", "Available again " + dayString);
                } else {
                    const dateString = nextAvailableDate.format('MMMM DD, YYYY');
                    this.setNotice("notice noticeLaterAnotherDay", "Available again " + dateString);
                }
            }
        }
    }
}
